/**
 * Utility functions per gestire la cache utente
 */

/**
 * Pulisce la cache utente per un determinato userId
 * @param {string} userId - ID dell'utente
 */
export const clearUserCache = (userId) => {
  try {
    localStorage.removeItem(`user_data_cache_${userId}`);
    localStorage.removeItem(`user_profile_cache_${userId}`);
    if (process.env.NODE_ENV === 'development') {
      console.log(`Cache utente pulita per UID: ${userId}`);
    }
  } catch (error) {
    console.error("Errore nella pulizia della cache utente:", error);
  }
};

/**
 * Pulisce tutta la cache utente (tutte le chiavi che iniziano con user_)
 */
export const clearAllUserCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('user_data_cache_') || key.startsWith('user_profile_cache_'))) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`Pulite ${keysToRemove.length} voci di cache utente`);
  } catch (error) {
    console.error("Errore nella pulizia completa della cache utente:", error);
  }
};

/**
 * Verifica se i dati utente nella cache sono validi
 * @param {string} userId - ID dell'utente
 * @returns {boolean} true se la cache è valida
 */
export const isUserCacheValid = (userId) => {
  try {
    const cachedData = localStorage.getItem(`user_data_cache_${userId}`);
    if (!cachedData) return false;

    const { data, timestamp } = JSON.parse(cachedData);
    const cacheAge = Date.now() - timestamp;

    return cacheAge < 5 * 60 * 1000 && data && typeof data === 'object' && data.email;
  } catch (error) {
    console.error("Errore nella verifica della cache utente:", error);
    return false;
  }
};

const SESSION_CACHE_KEY = 'user_session_cache';
const SESSION_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 ore

/**
 * Salva i dati utente nella cache persistente per la sessione cross-browser
 * @param {string} userId - ID dell'utente
 * @param {object} userData - Dati dell'utente da Firestore
 */
export const saveUserToCache = (userId, userData) => {
  try {
    if (!userId || !userData) return;
    const cacheEntry = {
      data: userData,
      userId,
      timestamp: Date.now(),
    };
    localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cacheEntry));
  } catch (error) {
    console.error("Errore nel salvataggio della cache sessione utente:", error);
  }
};

/**
 * Recupera i dati utente dalla cache persistente
 * @param {string} userId - ID dell'utente
 * @returns {object|null} Dati utente dalla cache, o null se scaduti/assenti
 */
export const getUserFromCache = (userId) => {
  try {
    const raw = localStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;

    const { data, userId: cachedUid, timestamp } = JSON.parse(raw);
    if (cachedUid !== userId) return null;

    const age = Date.now() - timestamp;
    if (age > SESSION_CACHE_TTL) {
      localStorage.removeItem(SESSION_CACHE_KEY);
      return null;
    }

    if (!data || typeof data !== 'object' || !data.email) return null;
    return data;
  } catch (error) {
    console.error("Errore nel recupero della cache sessione utente:", error);
    return null;
  }
};

/**
 * Rimuove la cache sessione persistente (da usare al logout)
 */
export const clearSessionCache = () => {
  try {
    localStorage.removeItem(SESSION_CACHE_KEY);
  } catch (error) {
    console.error("Errore nella pulizia della cache sessione:", error);
  }
};
